import React from 'react';
import { ColorRepresentation, RepeatWrapping, TextureLoader, Vector2 } from 'three';

import { Component, Member } from '@paragon/contract-library';
import { calculateMemberPolygon, calculateMemberThreeJsGeometry, getBoundsFromPoints } from '@paragon/three-shared';

import woodTextureUrl from '../resources/WoodFine0010.jpg';
import LabelVisual from './LabelVisual';

function renderLabel(showLabel: boolean | undefined, member: Member, component: Component) {
  if (!showLabel) return null;

  const memberBounds = getBoundsFromPoints(calculateMemberPolygon(member, component)!.vertices);
  const { size, position } = memberBounds;

  const labelPosition =
    size.height > size.width
      ? { x: position.x + 3.5, y: position.y, z: 0 }
      : { x: position.x, y: position.y + 3.5, z: 0 };

  return <LabelVisual labelText={member.name} position={labelPosition} />;
}

interface Props {
  readonly member: Member;
  readonly component: Component;

  readonly showLabel: boolean | undefined;
  readonly showEdges: boolean | undefined;

  readonly style: 'wood' | ColorRepresentation;
}

const defaultColor = 0xe0bb96;
const memberEdgeColor = 0x809080;

const woodTexture = new TextureLoader().load(woodTextureUrl, (loadedTexture) => {
  loadedTexture.anisotropy = 16;
  loadedTexture.repeat = new Vector2(0.1, 0.1);
  loadedTexture.wrapS = RepeatWrapping;
  loadedTexture.wrapT = RepeatWrapping;
});

const NEVER_RAYCAST = () => {};

export default function MemberVisual(props: Props) {
  const { member, component, showLabel } = props;
  const threeJsGeometry = calculateMemberThreeJsGeometry(member, component) ?? undefined;

  return (
    <>
      <mesh geometry={threeJsGeometry}>
        {props.style === 'wood' ? (
          <meshBasicMaterial color={defaultColor} map={woodTexture} />
        ) : (
          <meshBasicMaterial color={props.style} />
        )}
      </mesh>

      {props.showEdges && (
        <lineSegments
          // under no circumstances should these edges be raycasted -- besides being useless, it would be bad for
          //   performance and causes horrible event fighting with roof plane faces
          raycast={NEVER_RAYCAST}
        >
          <lineBasicMaterial color={memberEdgeColor} />
          <edgesGeometry attach="geometry" args={[threeJsGeometry ?? undefined]} />
        </lineSegments>
      )}

      {renderLabel(showLabel, member, component)}
    </>
  );
}
